import { t } from "i18next";
import { ArrowUpRight, Check, Puzzle, Route } from "lucide-react";
import type { JSX } from "react";
import { useCallback, useEffect, useState } from "react";
import { V3Button } from "@/components/ui/v3/button";
import {
  CHROME_WEB_STORE_URL,
  detectChromeExtension,
} from "@/lib/chrome-extension";
import { cn } from "@/lib/utils";
import { SettingsCard } from "./redesign/SettingsCard";

type ExtensionStatus = "checking" | "installed" | "notInstalled";

function statusTitleKey(
  status: ExtensionStatus,
):
  | "settings.chromeExtension.status.checking"
  | "settings.chromeExtension.status.installed"
  | "settings.chromeExtension.status.notInstalled" {
  if (status === "checking") return "settings.chromeExtension.status.checking";
  if (status === "installed")
    return "settings.chromeExtension.status.installed";
  return "settings.chromeExtension.status.notInstalled";
}

export const ChromeExtension = (): JSX.Element => {
  const [status, setStatus] = useState<ExtensionStatus>("checking");
  const [version, setVersion] = useState<string | undefined>();

  const refreshStatus = useCallback((): void => {
    detectChromeExtension()
      .then((result) => {
        setStatus(result.installed ? "installed" : "notInstalled");
        setVersion(result.version);
      })
      .catch(() => {
        setStatus("notInstalled");
        setVersion(undefined);
      });
  }, []);

  useEffect(() => {
    refreshStatus();

    const onVisible = (): void => {
      if (document.visibilityState === "visible") {
        refreshStatus();
      }
    };
    const onFocus = (): void => {
      refreshStatus();
    };

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);
    return (): void => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
    };
  }, [refreshStatus]);

  const features = [
    t("settings.chromeExtension.features.oneClick"),
    t("settings.chromeExtension.features.emrSupport"),
    t("settings.chromeExtension.features.saveTime"),
    t("settings.chromeExtension.features.quickSetup"),
  ];
  const steps = ["step1", "step2", "step3"] as const;

  return (
    <div className="flex flex-col gap-5 sm:gap-4">
      <SettingsCard
        aside={
          status === "checking" ? null : (
            <span
              className={cn(
                "inline-flex h-6 items-center gap-1.5 rounded-full px-2.5 text-[12px] font-semibold",
                status === "installed"
                  ? "bg-v3-ok-tint text-v3-ok-text"
                  : "bg-v3-surface-2 text-v3-text-2 shadow-v3-hairline",
              )}
              data-testid="chrome-extension-status"
            >
              {status === "installed" && (
                <span aria-hidden className="size-1.5 rounded-full bg-v3-ok" />
              )}
              {t(statusTitleKey(status))}
            </span>
          )
        }
        description={
          status === "checking"
            ? t(statusTitleKey(status))
            : status === "installed"
              ? t("settings.chromeExtension.status.installedDesc", {
                  version: version ? ` (v${version})` : "",
                })
              : t("settings.chromeExtension.status.notInstalledDesc")
        }
        icon={
          status === "installed" ? (
            <Check aria-hidden className="text-v3-ok-text" />
          ) : (
            <Puzzle aria-hidden />
          )
        }
        labelledBy="chrome-extension-status-title"
        testId="chrome-extension-status-card"
        title={t("settings.chromeExtension.status.title")}
      >
        <ul className="grid gap-x-4 gap-y-2 text-[14px] text-v3-text-2 sm:grid-cols-2">
          {features.map((feature) => (
            <li key={feature} className="flex items-center gap-2">
              <Check aria-hidden className="size-4 shrink-0 text-v3-text-3" />
              {feature}
            </li>
          ))}
        </ul>
        {status === "notInstalled" && (
          <V3Button
            className="mt-4"
            variant="primary"
            onClick={() => window.open(CHROME_WEB_STORE_URL, "_blank")}
          >
            {t("settings.chromeExtension.status.installButton")}
            <ArrowUpRight aria-hidden />
          </V3Button>
        )}
      </SettingsCard>
      <SettingsCard
        icon={<Route aria-hidden />}
        labelledBy="chrome-extension-how"
        title={t("settings.chromeExtension.howItWorks.title")}
      >
        <ol className="space-y-3">
          {steps.map((step, index) => (
            <li key={step} className="flex gap-3">
              <span
                aria-hidden
                className="flex size-6 shrink-0 items-center justify-center rounded-full bg-v3-surface-2 text-[12px] font-semibold text-v3-text-2 shadow-v3-hairline"
              >
                {index + 1}
              </span>
              <span className="min-w-0 pt-0.5">
                <span className="block text-[14px] font-semibold">
                  {t(`settings.chromeExtension.howItWorks.${step}.title`)}
                </span>
                <span className="block text-[13.5px] leading-snug text-v3-text-2">
                  {t(`settings.chromeExtension.howItWorks.${step}.desc`)}
                </span>
              </span>
            </li>
          ))}
        </ol>
      </SettingsCard>
    </div>
  );
};
